import { useState } from "react";
import { Button } from "../../../../../components/buttons/button";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { YouCard } from "../../../../../components/cards/youCard";
import { SearchInput } from "../../../../../components/inputs/searchInput";
import { BulkEntryModal } from "../../../../../components/modals/app_modals/bulkEntry/bulkEntryModal";
import { AdminUploadContributionModal } from "../../../../../components/modals/app_modals/contributions/adminUploadContributionModal";
import { CooperativeContributionsHistoryModal } from "../../../../../components/modals/app_modals/contributions/cooperativeContributionsHistoryModal";
import { SuccessModal } from "../../../../../components/modals/app_modals/successModal";
import { TableComponent } from "../../../../../components/table/table";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import { usePermissions } from "../../../../../customHooks/usePermissions";
import {
  formatDate,
  formatToCurrency,
  getOffsetFromPage,
  getPageFromOffset,
} from "../../../../../helperFunctions";
import {
  useGetCooperativeContributionsDetailsQuery,
  useNewAdminBulkUploadContributionMutation,
} from "../../../../../redux/features/contributions/contributions.api";
import { MemberContributionsDetailsQuery } from "../../../../../redux/features/contributions/contributions.type";
import {
  SingleCooperativeDetails,
  SingleMemberContributionDetails,
} from "../../../../../redux/features/cooperative/cooperative.type";
import { PermissionCode } from "../../../../../redux/features/settings/settings.type";
import { useAppSelector } from "../../../../../redux/hooks";

export const CooperativeOfficialContributionsView = ({
  isProductActive = true,
  permissions,
  cooperativeDetails,
}: {
  isProductActive: boolean;
  permissions: PermissionCode[];
  cooperativeDetails: SingleCooperativeDetails;
}) => {
  const [query, setQuery] = useState<MemberContributionsDetailsQuery>({
    coop: cooperativeDetails.id,
    logs: true,
    text: undefined,
    limit: 10,
    offset: 0,
  });
  const [selectedMemberContribution, setSelectedMemberContribution] =
    useState<SingleMemberContributionDetails>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const { hasPermissionsFor } = usePermissions();

  const { profile } = useAppSelector((state) => {
    return state.auth;
  });

  const {
    data: cooperativeContributions,
    isLoading: loadingCooperativeContributions,
    isFetching: fetchingCooperativeContributions,
  } = useGetCooperativeContributionsDetailsQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  const [doBulkUpload, { isLoading: uploading }] =
    useNewAdminBulkUploadContributionMutation();

  return (
    <>
      <div className="w-full flex flex-col gap-6">
        <NumberCard
          title={"Total Contributions Balance"}
          value={cooperativeContributions?.data?.summary}
          primaryButtonText={"Direct Debit"}
          onPrimaryButtonClick={
            cooperativeDetails.Settings.settings.isDirectDebitEnabled
              ? () => {
                  // handleModalOpen("adminBulkUploadContributionModal");
                  handleModalOpen("directDebitModal");
                }
              : undefined
          }
          primaryButtonVariant="primary-white"
          primaryButtonIcon={"card"}
          primaryButtonDisabled={
            !hasPermissionsFor(["can.add.payment"], permissions)
          }
          secondaryButtonText={"Bulk Upload"}
          onSecondaryButtonClick={() => {
            handleModalOpen("bulkEntryModal");
          }}
          secondaryButtonVariant={
            cooperativeDetails.Settings.settings.isDirectDebitEnabled
              ? "secondary-white"
              : "primary-white"
          }
          secondaryButtonIcon={"file-upload-icon"}
          secondaryButtonDisabled={false}
          loading={loadingCooperativeContributions}
        />

        <div className="flex items-center flex-wrap gap-2">
          <h3 className="text-base font-medium flex-1">
            Contributions Per Member
          </h3>

          <SearchInput
            placeholder="Search member name"
            searchFunction={(val) => {
              setQuery({ ...query, text: val });
            }}
            className="w-full md:max-w-[286px] !px-4 !py-2"
            iconDimension={16}
          />

          <Button
            onClick={() => {
              handleModalOpen("cooperativeContributionsHistoryModal");
            }}
            text="View History"
            type="button"
            className="w-full md:!max-w-[140px] !text-xs"
            loading={false}
            disabled={false}
            icon={"refresh"}
            variation="primary"
            size="small"
          />
        </div>

        <TableComponent
          className=""
          stickyLastCol
          mobileCardView={false}
          headers={["Member", `Balance`]}
          headerActionProps={{
            hasMoreMenu: hasPermissionsFor(["can.add.payment"], permissions),
          }}
          rows={
            cooperativeContributions?.data?.member?.results?.map(
              (member, idx) => {
                const key = `member-contribution-${idx}`;

                const isUser = profile?.Email === member?.MemberID?.User?.Email;

                return {
                  id: member?.id,
                  items: [
                    <div
                      className="flex gap-2 items-center min-w-fit whitespace-nowrap"
                      key={`${key}-details`}
                    >
                      {`${member?.MemberID?.User?.FirstName} ${member?.MemberID?.User?.LastName}`}{" "}
                      {isUser ? <YouCard /> : undefined}
                    </div>,

                    formatToCurrency(member?.Balance, {
                      currency: "NGN",
                      decimalPlaces: 2,
                    }),
                  ].filter((t) => t !== undefined),
                  actionProps: {
                    hasActions: true,
                    hasMoreMenu: hasPermissionsFor(
                      ["can.add.payment"],
                      permissions
                    ),
                    moreOptions: [
                      {
                        icon: "plus",
                        text: "Add Contribution",
                        onClick: () => {
                          setSelectedMemberContribution(member);
                          handleModalOpen("adminUploadContributionModal");
                        },
                        hidden: !isProductActive,
                      },
                    ],
                  },
                };
              }
            ) ?? []
          }
          isPaginated
          isLoading={fetchingCooperativeContributions}
          isTableEmpty={[0, undefined].includes(
            cooperativeContributions?.data?.member?.results?.length
          )}
          currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
          onPageChange={(page) => {
            setQuery({
              ...query,
              offset: getOffsetFromPage(page, query.limit ?? 0),
            });
          }}
          totalCount={cooperativeContributions?.data?.member?.count}
          emptrySrc="/assets/images/empty_state_cooperatives.svg"
          emptyTitle="No Contributions Found"
          itemsPerPage={query?.limit}
        />
      </div>

      {selectedMemberContribution ? (
        <AdminUploadContributionModal
          modal={modal}
          modalOpen={modalOpen && modal === "adminUploadContributionModal"}
          handleModalClose={handleModalClose}
          handleModalOpen={handleModalOpen}
          cooperativeDetails={cooperativeDetails}
          onSuccess={async () => {
            // handleModalClose();
          }}
          memberContribution={selectedMemberContribution}
        />
      ) : undefined}

      <CooperativeContributionsHistoryModal
        isModalOpen={
          modalOpen && modal === "cooperativeContributionsHistoryModal"
        }
        onModalClose={() => {
          handleModalClose();
        }}
        cooperativeDetails={cooperativeDetails}
      />

      {/* <BulkUploadContributionsModal
          modal={modal}
          modalOpen={modalOpen}
          handleModalClose={handleModalClose}
          handleModalOpen={handleModalOpen}
          cooperativeDetails={cooperativeDetails}
          onSuccess={async () => {
            // handleModalClose();
          }}
        /> */}

      <BulkEntryModal
        modalOpen={modalOpen && modal === "directDebitModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        onSuccess={() => {}}
        type="debit"
        title="Contributions Direct Debit"
      />

      <BulkEntryModal
        modalOpen={modalOpen && modal === "bulkEntryModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        onSuccess={async ({ data, name, close, reset }) => {
          const resp = await doBulkUpload({
            unique: "email",
            coop: cooperativeDetails.id,
            filename: name,
            logs: data.map((d) => {
              return {
                paymentDate: formatDate(d.paymentDate, "YYYY-MM-DD"),
                amount: d.amount,
                mode: d.paymentMethod!,
                payslip: d.paymentProof!,
                email: d.memberEmail,
              };
            }),
          });

          if (resp.data?.success) {
            close();
            reset();
            handleModalOpen("bulkEntrySuccessModal");
          }
        }}
        type="upload"
        title="Bulk Contributions Upload"
        loading={uploading}
      />

      <SuccessModal
        isModalOpen={modalOpen && modal === "bulkEntrySuccessModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        message="Bulk Contributions Uploaded Successfully"
        subText=""
      />
    </>
  );
};
